import React from "react";
import { useTenantScope } from "../scope/TenantScopeContext";
import "../styles/tenantScope.css";

function ScopeBar({
  showDevice = true,
  showPlatform = true,
  showDateRange = true,
  showStatus = true,
  showSeverity = true,
  statusOptions = [
    "all","online","offline","active","resolved","pending","open","closed",
  ],
  severityOptions = [
    "all","critical","high","medium","low","info",
  ],
  onRefresh,
}) {
  const scope = useTenantScope();

  const refresh = async () => {
    await scope.refreshScope();
    if (onRefresh) await onRefresh();
  };

  return (
    <section className="wp-scope-bar no-print" aria-label="Dashboard data scope">
      <div className="wp-scope-field">
        <label htmlFor="wp-company-scope">Company</label>
        {scope.isSuperAdmin ? (
          <select
            id="wp-company-scope"
            value={scope.companyId || ""}
            onChange={(event) => scope.setCompanyId(event.target.value)}
          >
            {scope.companies.length === 0 && (
              <option value={scope.companyId || ""}>
                {scope.companyId
                  ? `Company ${scope.companyId}`
                  : "Loading companies…"}
              </option>
            )}
            {scope.companies.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {company.company_name ||
                  company.name ||
                  `Company ${company.id}`}
              </option>
            ))}
          </select>
        ) : (
          <div className="wp-scope-locked">
            {scope.selectedCompany?.company_name ||
              scope.selectedCompany?.name ||
              `Company ${scope.companyId || "unassigned"}`}
          </div>
        )}
      </div>

      {showPlatform && (
        <div className="wp-scope-field">
          <label htmlFor="wp-platform-scope">Platform</label>
          <select
            id="wp-platform-scope"
            value={scope.platform || "all"}
            onChange={(event) => scope.setPlatform(event.target.value)}
          >
            <option value="all">All platforms</option>
            <option value="windows">Windows</option>
            <option value="macos">macOS</option>
            <option value="linux">Linux</option>
            <option value="android">Android</option>
            <option value="ios">iOS / iPadOS</option>
          </select>
        </div>
      )}

      {showDevice && (
        <div className="wp-scope-field">
          <label htmlFor="wp-device-scope">Device</label>
          <select
            id="wp-device-scope"
            value={scope.deviceUuid || ""}
            onChange={(event) => scope.setDeviceUuid(event.target.value)}
            disabled={!scope.companyId}
          >
            <option value="">All devices</option>
            {(scope.scopedDevices || scope.devices).map((device) => (
              <option key={device.device_uuid} value={device.device_uuid}>
                {device.hostname ||
                  device.device_name ||
                  `Device ${device.id}`}{" "}
                · {device.platform || "Unknown"}
              </option>
            ))}
          </select>
        </div>
      )}

      {showDateRange && (
        <>
          <div className="wp-scope-field">
            <label htmlFor="wp-start-date">From</label>
            <input
              id="wp-start-date"
              type="date"
              value={scope.startDate || ""}
              max={scope.endDate || undefined}
              onChange={(event) => scope.setStartDate(event.target.value)}
            />
          </div>
          <div className="wp-scope-field">
            <label htmlFor="wp-end-date">To</label>
            <input
              id="wp-end-date"
              type="date"
              value={scope.endDate || ""}
              min={scope.startDate || undefined}
              onChange={(event) => scope.setEndDate(event.target.value)}
            />
          </div>
        </>
      )}

      {showStatus && (
        <div className="wp-scope-field">
          <label htmlFor="wp-status-scope">Status</label>
          <select
            id="wp-status-scope"
            value={scope.status}
            onChange={(event) => scope.setStatus(event.target.value)}
          >
            {statusOptions.map((value) => (
              <option key={value} value={value}>
                {value === "all" ? "All statuses" : value}
              </option>
            ))}
          </select>
        </div>
      )}

      {showSeverity && (
        <div className="wp-scope-field">
          <label htmlFor="wp-severity-scope">Severity</label>
          <select
            id="wp-severity-scope"
            value={scope.severity}
            onChange={(event) => scope.setSeverity(event.target.value)}
          >
            {severityOptions.map((value) => (
              <option key={value} value={value}>
                {value === "all" ? "All severities" : value}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="wp-scope-actions">
        <button
          type="button"
          onClick={refresh}
          disabled={scope.loadingScope}
          title="Reload the module using the selected scope."
        >
          {scope.loadingScope ? "Refreshing…" : "Apply / Refresh"}
        </button>
        <button type="button" onClick={scope.resetFilters}>
          Reset Filters
        </button>
      </div>

      {scope.scopeError && (
        <div className="wp-scope-error" role="alert">
          {scope.scopeError}
        </div>
      )}
    </section>
  );
}

export default ScopeBar;
